import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ErrorMessage, Loading } from '@Components';
import { CredentialsForm } from '@Components/CredentialsForm';
import { useFetchData } from '@CustomHooks';
import { ConnectorConfig, ProviderCredential } from '@Models';
import { credentialIntakeApi } from '@Network';
import { adaptConnectorsFromApi } from '../Credentials/connectorRegistry';

// Public, token-gated page for a brand-new client (no Auth0 login yet) to
// submit connector credentials — replaces the external WebFlow form. The
// token is minted by an admin action and identifies which client this is,
// standing in for the Auth0 login the authenticated Credentials page relies on.
const CredentialIntake = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const { data, isLoading, error } = useFetchData(credentialIntakeApi.getConnectors, token, !token);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [submittedTypes, setSubmittedTypes] = useState<string[]>([]);

  const connectorMap = useMemo<Record<string, ConnectorConfig>>(
    () => (data ? adaptConnectorsFromApi(data, t) : {}),
    [data, t],
  );

  if (!token) {
    return (
      <ErrorMessage
        internalMessage="Credential intake page opened without a token"
        message="CredentialIntake.InvalidLink"
      />
    );
  }

  if (isLoading) return <Loading title={t('CredentialIntake.Loading')} />;

  if (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    const message = status === 401 || status === 403 ? 'CredentialIntake.InvalidLink' : 'Global.ErrorMessage';
    return (
      <ErrorMessage
        internalMessage={`Unable to load credential intake connectors: ${String(error)}`}
        message={message}
      />
    );
  }

  const connectorEntries = Object.entries(connectorMap).filter(([, connectorConfig]) => !connectorConfig.readOnly);
  const selectedConfig = selectedType ? connectorMap[selectedType] : undefined;

  return (
    <div className="mx-auto flex min-h-screen max-w-4xl flex-col gap-6 p-8">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">{t('CredentialIntake.Title')}</h1>
        <p className="text-sm text-gray-500">{t('CredentialIntake.Description')}</p>
      </div>

      {selectedConfig && selectedType ? (
        <CredentialsForm
          connectorName={selectedConfig.name}
          connectorType={selectedType}
          fields={selectedConfig.fields}
          mode="create"
          onCancel={() => setSelectedType(null)}
          onSuccess={() => {
            setSubmittedTypes((previous) => [...previous, selectedType]);
            setSelectedType(null);
          }}
          submitCreate={(credential: ProviderCredential) => credentialIntakeApi.submitCredential(token, credential)}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {connectorEntries.map(([connectorType, connectorConfig]) => {
            const alreadySubmitted = submittedTypes.includes(connectorType);
            return (
              <button
                key={connectorType}
                type="button"
                disabled={alreadySubmitted}
                onClick={() => setSelectedType(connectorType)}
                className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 text-left hover:bg-gray-50 disabled:opacity-50">
                <span className="font-medium text-gray-900">{connectorConfig.name}</span>
                {alreadySubmitted && <span className="text-xs text-brand-600">{t('CredentialIntake.Submitted')}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CredentialIntake;
